/**
 * The email a pre-launch form was prepared from, and whether it is the one
 * selected in Outlook right now. Together they decide whether the form offers
 * Open Email: a brief opened from Ready to start — typically while an ora
 * notification is the selected item — is NOT the email on screen, and the link
 * is the only way back to it. Opening the email already on screen would just
 * pop out a second copy, so there the link is withheld.
 */

/** The email an open pre-launch form is about. */
export type SourceEmail = {
  /** Graph id handed to `openMessageInOutlook` (REST or EWS form). */
  graphMessageId: string;
  /** RFC 5322 Message-ID, when known. */
  internetMessageId: string | null;
};

/** The selected Outlook item's identity, as `useMailboxItem` reports it. */
export type SelectedEmail = {
  internetMessageId?: string | null;
  /** EWS item id. */
  emailId?: string | null;
  /** REST v2 item id. */
  restId?: string | null;
};

/**
 * Capture the form's email at open. A brief carries its own identity; without
 * one, the form is about the selected item. `null` when there is no id to open.
 */
export function sourceEmailFor(
  brief: { internetMessageId: string; graphMessageId?: string | null } | undefined,
  selected: SelectedEmail | null | undefined,
): SourceEmail | null {
  if (brief) {
    return brief.graphMessageId
      ? {
          graphMessageId: brief.graphMessageId,
          internetMessageId: brief.internetMessageId,
        }
      : null;
  }
  return selected?.emailId
    ? {
        graphMessageId: selected.emailId,
        internetMessageId: selected.internetMessageId ?? null,
      }
    : null;
}

/**
 * Same key as `ReadyBriefsQueue`'s current-email filter (the RFC 5322 id). The
 * item ids are only a fallback, and both are tried because the brief's Graph
 * id may arrive in either format.
 */
export function isSelectedEmail(
  source: SourceEmail,
  selected: SelectedEmail | null | undefined,
): boolean {
  if (!selected) return false;
  if (source.internetMessageId && selected.internetMessageId) {
    return source.internetMessageId === selected.internetMessageId;
  }
  return (
    source.graphMessageId === selected.emailId
    || source.graphMessageId === selected.restId
  );
}
